import { useEffect, useState, useCallback } from "react";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import {
  MessageSquare, Phone, Eye, CheckCircle2, XCircle, StickyNote, Trash2,
  Send, Reply, Trophy, Loader2, ChevronDown, ChevronUp, X,
  ExternalLink, Quote,
} from "lucide-react";
import { Link } from "react-router-dom";
import TestimonialModal from "@/components/TestimonialModal";

const KIND_META = {
  sent:    { label: "Sent",     color: "#0EA5E9", Icon: Send },
  viewed:  { label: "Viewed",   color: "#94A3B8", Icon: Eye },
  replied: { label: "Replied",  color: "#F59E0B", Icon: Reply },
  won:     { label: "Won",      color: "#10B981", Icon: Trophy },
  lost:    { label: "Lost",     color: "#E76F51", Icon: XCircle },
  note:    { label: "Note",     color: "#6366F1", Icon: StickyNote },
};

const CHANNEL_ICON = {
  sms: MessageSquare,
  call: Phone,
  email: MessageSquare,
};

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function fireConfetti() {
  // Two bursts from the left + right for a wholesaler-paid celebration.
  const opts = { particleCount: 80, startVelocity: 55, spread: 75, ticks: 200, scalar: 1.05 };
  confetti({ ...opts, origin: { x: 0.18, y: 0.65 }, angle: 60, colors: ["#10B981", "#A7F3D0", "#0EA5E9"] });
  confetti({ ...opts, origin: { x: 0.82, y: 0.65 }, angle: 120, colors: ["#10B981", "#FDE68A", "#6366F1"] });
}

export default function DispoTimeline({ leadId, embedded = false, defaultOpen = true }) {
  const [events, setEvents] = useState(null);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(defaultOpen);
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState({});
  const [winPublished, setWinPublished] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [testimonialOf, setTestimonialOf] = useState(null);     // event being quoted
  const [currentTestimonial, setCurrentTestimonial] = useState(null);

  const refresh = useCallback(async () => {
    const [e, s, l] = await Promise.all([
      api.get(`/leads/${leadId}/dispo`),
      api.get(`/leads/${leadId}/dispo/summary`),
      api.get(`/leads/${leadId}`).catch(() => ({ data: null })),
    ]);
    setEvents(e.data);
    setSummary(s.data);
    if (l.data) {
      setWinPublished(!!l.data.published_to_wins);
      setCurrentTestimonial(l.data.testimonial || null);
    }
  }, [leadId]);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async (payload, { celebrate } = {}) => {
    setBusy((b) => ({ ...b, [payload.kind]: true }));
    try {
      await api.post(`/leads/${leadId}/dispo`, payload);
      if (celebrate) fireConfetti();
      refresh();
    } finally {
      setBusy((b) => ({ ...b, [payload.kind]: false }));
    }
  };

  const remove = async (id) => {
    if (id.startsWith("click-")) return; // auto events are read-only
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/dispo/${id}`);
    refresh();
  };

  const wonCount = summary?.counts?.won || 0;

  return (
    <section
      className={"card " + (embedded ? "" : "mt-6")}
      style={{ borderColor: wonCount > 0 ? "#10B981" : undefined, borderWidth: wonCount > 0 ? 2 : undefined }}
      data-testid="dispo-timeline"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 group text-left flex-1 min-w-0"
          data-testid="dispo-toggle"
        >
          <Trophy size={14} style={{ color: wonCount > 0 ? "#10B981" : "var(--ink-3)" }} />
          <span className="label-eyebrow">Dispo timeline</span>
          {summary && <DispoCounts summary={summary} />}
          {open ? <ChevronUp size={14} className="ml-auto text-[var(--ink-3)]" /> : <ChevronDown size={14} className="ml-auto text-[var(--ink-3)]" />}
        </button>
      </div>

      {open && (
        <div className="mt-4">
          {/* Win Story publish CTA — only when this lead has a WON event */}
          {summary?.won && (
            <div
              className="rounded-xl p-3 mb-4 border flex items-center gap-3 flex-wrap"
              style={{ background: "rgba(16,185,129,0.07)", borderColor: "rgba(16,185,129,0.35)" }}
              data-testid="dispo-publish-cta"
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#10B98122", color: "#10B981" }}>
                <Trophy size={15} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm">
                  {winPublished ? "Published to your Wins page" : "Make this win count twice"}
                </div>
                <div className="text-xs text-[var(--ink-2)] mt-0.5">
                  {winPublished
                    ? "City, ARV, fee + time-to-close are showing publicly. No buyer/seller info."
                    : "Drop this deal on your public Win Story — instant social proof for cash buyers."}
                </div>
              </div>
              <button
                type="button"
                className={winPublished ? "btn-outline" : "btn-money"}
                onClick={async () => {
                  setPublishing(true);
                  try {
                    await api.patch(`/leads/${leadId}/publish-win`, { enabled: !winPublished });
                    setWinPublished((v) => !v);
                  } finally { setPublishing(false); }
                }}
                disabled={publishing}
                data-testid="dispo-publish-toggle"
              >
                {publishing ? <Loader2 size={13} className="animate-spin" /> : <Trophy size={13} />}
                {winPublished ? "Unpublish" : "Publish to wins"}
              </button>
              <Link to="/v3/growth/wins" className="btn-ghost !py-1.5 !px-2 text-xs" data-testid="dispo-publish-manage">
                Manage <ExternalLink size={11} />
              </Link>
            </div>
          )}

          {/* Quick action row */}
          <div className="flex gap-2 flex-wrap mb-4">
            <button type="button" className="btn-outline" onClick={() => create({ kind: "replied", channel: "sms" })} disabled={busy.replied} data-testid="dispo-add-replied">
              {busy.replied ? <Loader2 size={13} className="animate-spin" /> : <Reply size={13} />}
              Log a reply
            </button>
            <button
              type="button"
              className="btn-money"
              style={{ background: "#10B981", borderColor: "#10B981" }}
              onClick={() => create({ kind: "won" }, { celebrate: true })}
              disabled={busy.won}
              data-testid="dispo-add-won"
            >
              {busy.won ? <Loader2 size={13} className="animate-spin" /> : <Trophy size={13} />}
              Mark WON
            </button>
            <button type="button" className="btn-outline text-[var(--warn)]" onClick={() => create({ kind: "lost" })} disabled={busy.lost} data-testid="dispo-add-lost">
              <XCircle size={13} /> Mark lost
            </button>
            <button type="button" className="btn-ghost" onClick={() => setAdding((a) => !a)} data-testid="dispo-add-note-toggle">
              <StickyNote size={13} /> {adding ? "Cancel" : "Add note"}
            </button>
          </div>

          {adding && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!note.trim()) return;
                await create({ kind: "note", notes: note.trim() });
                setNote("");
                setAdding(false);
              }}
              className="mb-4 flex gap-2"
              data-testid="dispo-note-form"
            >
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Quick note — e.g. 'Maya wants to see photos before committing'"
                maxLength={500}
                className="form-input flex-1"
                data-testid="dispo-note-input"
                autoFocus
              />
              <button type="submit" className="btn-money" data-testid="dispo-note-save">Save</button>
            </form>
          )}

          {/* Timeline */}
          {!events ? (
            <div className="text-sm text-[var(--ink-2)] py-4">Loading…</div>
          ) : events.length === 0 ? (
            <div className="text-sm text-[var(--ink-2)] py-6 text-center">
              <Send size={20} className="mx-auto text-[var(--ink-3)] mb-2" />
              No outreach logged yet — text a matched buyer from the Inbox to start the timeline.
            </div>
          ) : (
            <ol className="space-y-2.5" data-testid="dispo-events-list">
              {events.map((e) => (
                <EventRow
                  key={e.id}
                  e={e}
                  onDelete={remove}
                  canQuote={summary?.won && (e.kind === "replied" || e.kind === "note") && !!(e.notes || "").trim()}
                  isQuoted={currentTestimonial?.source_event_id === e.id}
                  onQuote={() => setTestimonialOf(e)}
                />
              ))}
            </ol>
          )}
        </div>
      )}

      {testimonialOf && (
        <TestimonialModal
          leadId={leadId}
          initial={currentTestimonial}
          sourceEventId={testimonialOf.id}
          defaultAttribution={
            testimonialOf.buyer_name
              ? `${testimonialOf.buyer_name.split(" ")[0]} · ${testimonialOf.kind === "replied" ? "cash buyer" : "buyer"}`
              : ""
          }
          onClose={() => setTestimonialOf(null)}
          onSaved={(t) => setCurrentTestimonial(t || null)}
        />
      )}
    </section>
  );
}

function DispoCounts({ summary }) {
  const items = [
    { key: "sent",    n: summary.counts?.sent || 0,    color: KIND_META.sent.color },
    { key: "viewed",  n: summary.views || 0,           color: KIND_META.viewed.color },
    { key: "replied", n: summary.counts?.replied || 0, color: KIND_META.replied.color },
    { key: "won",     n: summary.counts?.won || 0,     color: KIND_META.won.color },
  ].filter((it) => it.n > 0);
  if (items.length === 0) return <span className="text-xs text-[var(--ink-3)]">no events yet</span>;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="dispo-counts">
      {items.map((it) => (
        <span
          key={it.key}
          className="chip"
          style={{ background: it.color + "22", color: it.color, borderColor: it.color + "55", fontSize: 10 }}
          data-testid={`dispo-count-${it.key}`}
        >
          <span className="chip-dot" style={{ background: it.color }} />
          {it.n} {it.key}
        </span>
      ))}
    </div>
  );
}

function EventRow({ e, onDelete, canQuote, isQuoted, onQuote }) {
  const meta = KIND_META[e.kind] || KIND_META.note;
  const ChannelIcon = CHANNEL_ICON[e.channel];
  const Icon = meta.Icon;
  const isAuto = e.source === "auto" || e.id?.startsWith("click-");
  return (
    <li
      className="flex items-start gap-3 rounded-lg p-3"
      style={{ background: e.kind === "won" ? "rgba(16,185,129,0.06)" : "rgba(15,23,42,0.03)" }}
      data-testid={`dispo-event-${e.id}`}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: meta.color + "22", color: meta.color }}
      >
        <Icon size={15} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="chip"
            style={{ background: meta.color + "22", color: meta.color, borderColor: meta.color + "55", fontSize: 10 }}
          >
            <span className="chip-dot" style={{ background: meta.color }} />
            {meta.label}
          </span>
          {e.buyer_name && <span className="font-semibold text-sm">{e.buyer_name}</span>}
          {ChannelIcon && <span className="text-[var(--ink-3)]"><ChannelIcon size={12} /></span>}
          {e.channel && !ChannelIcon && (
            <span className="text-[10px] uppercase tracking-widest text-[var(--ink-3)]">{e.channel}</span>
          )}
          {isAuto && <span className="text-[10px] uppercase tracking-widest text-[var(--ink-3)]">auto</span>}
          {isQuoted && (
            <span
              className="chip"
              style={{ background: "#10B98122", color: "#10B981", borderColor: "#10B98155", fontSize: 9 }}
              data-testid={`dispo-event-quoted-${e.id}`}
            >
              <Quote size={9} /> testimonial
            </span>
          )}
        </div>
        {e.notes && <div className="text-sm text-[var(--ink-2)] mt-1">{e.notes}</div>}
        <div className="text-xs text-[var(--ink-3)] mt-1 flex items-center gap-3 flex-wrap">
          <span>{timeAgo(e.created_at)}</span>
          {canQuote && (
            <button
              type="button"
              onClick={onQuote}
              className="inline-flex items-center gap-1 font-semibold text-[10px] uppercase tracking-widest hover:underline"
              style={{ color: isQuoted ? "#10B981" : "#0EA5E9" }}
              data-testid={`dispo-event-quote-${e.id}`}
            >
              <Quote size={10} /> {isQuoted ? "Edit testimonial" : "Use as testimonial"}
            </button>
          )}
        </div>
      </div>
      {!isAuto && (
        <button
          type="button"
          onClick={() => onDelete(e.id)}
          className="text-[var(--ink-3)] hover:text-[var(--warn)] shrink-0"
          aria-label="Delete event"
          data-testid={`dispo-delete-${e.id}`}
        >
          <X size={14} />
        </button>
      )}
    </li>
  );
}
